import React, { useState } from "react";
import { API, REPORT_URL, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileBarChart, Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const REPORTS = [
  ["daily", "Daily Report"], ["dp-earnings", "DP Earnings Report"], ["ev-rental", "EV Rental Report"],
  ["outstanding", "Outstanding Report"], ["incentive", "Incentive Report"], ["asset", "Asset Report"],
  ["onboarding", "Onboarding Report"],
];

export default function ExportsTab() {
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = (kind) => {
    window.open(REPORT_URL(kind, "fmt=csv"), "_blank");
    toast.success(`Exporting ${kind} report (CSV)`);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bulk/validate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setValidation(data);
      toast[data.errors.length ? "warning" : "success"](`${data.valid.length} valid, ${data.errors.length} errors`);
    } catch (e) { toast.error("Validation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map(([k, l]) => (
          <div key={k} className="flex items-center justify-between rounded-xl border border-border bg-card p-4" data-testid={`report-${k}`}>
            <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileBarChart className="h-5 w-5" /></div><p className="text-sm font-semibold">{l}</p></div>
            <Button size="sm" variant="outline" onClick={() => download(k)} data-testid={k === "dp-earnings" ? "export-report-excel-btn" : `export-${k}`}><Download className="mr-1 h-4 w-4" /> CSV</Button>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <h3 className="font-head text-sm font-semibold flex items-center gap-2"><Upload className="h-4 w-4" /> Bulk Upload Daily Orders (CSV)</h3>
        <p className="mt-1 text-xs text-muted-foreground">Columns: Date, FE ID, DP Name, Location, Orders, Distance, Rain Charges, Extra Incentive, Remarks. Validates invalid FE ID, duplicates, missing orders.</p>
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 font-medium hover:bg-accent"><FileSpreadsheet className="h-4 w-4" /> {busy ? "Validating…" : "Choose CSV file"}</span>
          <input type="file" accept=".csv" className="hidden" onChange={onFile} data-testid="bulk-upload-input" />
        </label>

        {validation && (
          <div className="mt-4">
            <div className="mb-2 flex gap-3 text-sm">
              <span className="flex items-center gap-1 text-emerald-500"><CheckCircle className="h-4 w-4" /> {validation.valid.length} valid</span>
              <span className="flex items-center gap-1 text-rose-500"><AlertTriangle className="h-4 w-4" /> {validation.errors.length} errors</span>
            </div>
            {validation.errors.length > 0 && (
              <div className="overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Row</TableHead><TableHead>FE ID</TableHead><TableHead>Date</TableHead><TableHead>Orders</TableHead><TableHead>Errors</TableHead></TableRow></TableHeader>
                  <TableBody>{validation.errors.map((e, i) => (
                    <TableRow key={i}><TableCell>{e.row}</TableCell><TableCell className="font-mono text-xs">{e.fe_id}</TableCell><TableCell>{e.date}</TableCell><TableCell>{e.orders}</TableCell><TableCell className="text-rose-500 text-xs">{e.errors.join(", ")}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
